import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Wrapper from '../Wrapper';
import baseStyles from '../../../styles/baseStyles.module.scss';
import styles from './styles/styles.module.scss';
import RegistrationForm from '../RegistrationForm';
import { ProPageStrings } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

import CheckCircleIcon from '../../../../assets/images/pro/greenCheck-filled.svg';
import BulletList from '../../shared/text/BulletList';
import { initializeOptimizely, trackOptimizelyEvent } from '../../../utils/optimizely_helpers';
import {
  OptimizelyExperiment,
  OptimizelyProvider,
  OptimizelyVariation,
} from '@optimizely/react-sdk';

const getText = isProPage => {
  const { registrationText, loginForm, formText, altLoginsText } = ProPageStrings;
  let text = {
    ...registrationText,
    ...loginForm,
    ...formText,
    ...altLoginsText
  };

  if (isProPage) {
    text = { ...text, ...ProPageStrings };
  }

  return text;
};

const handleOptimizelyEvent = (optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes) => {
  if (optimizelyInstance) {
    trackOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes);
  }
};

const FormFooter = ({ text }) => {
  return (
    <div className={baseStyles.info}>
      {text.privacyPolicyLead}{' '}
      <a className={baseStyles.route} href="/terms">
        {text.termsLink}
      </a>{' '}
      {text.and}{' '}
      <a className={baseStyles.route} href="/privacy">
        {text.privacy}
      </a>
      .
    </div>
  );
};

const OptimizelyMockSignupPageTest = ({ optimizelyInstance }) => {
  if (!optimizelyInstance) {
    return null;
  }
  const featuresList = [
    "Keep track of your favorite trails",
    "Record and share your activity",
    "Create and share custom maps"
  ];
  return (
    <OptimizelyProvider optimizely={optimizelyInstance} isServerSide={true}>
      <OptimizelyExperiment experiment="mock_signup_page_test">
        <OptimizelyVariation variation="variation_description">
          <BulletList bullet={<img src={CheckCircleIcon} style={{ color: '#428a13' }} />} classes={styles.bulletList} list={featuresList} />
        </OptimizelyVariation>
        <OptimizelyVariation variation="variation_control">
        </OptimizelyVariation>
        <OptimizelyVariation default>
        </OptimizelyVariation>
      </OptimizelyExperiment>
    </OptimizelyProvider>
  );
};

const SignUpForm = ({
  isProPage,
  locale,
  mobileBrowser,
  optimizelySettings,
  countryId,
  handleRegistrationSuccess,
  redirectTo,
  handleLoginClick,
  enableRecaptcha,
  facebookAppId,
  googleClientId
}) => {
  const [optimizelyInstance, updateOptimizelyInstance] = useState(null);
  const [optimizelyUserId, updateOptimizelyUserId] = useState(null);
  useEffect(() => {
    const [_optimizelyInstance, _optimizelyUserId] = initializeOptimizely(optimizelySettings);
    if (_optimizelyInstance) {
      const optimizelyAttributes = { locale, mobileBrowser };
      _optimizelyInstance.setUser({ id: _optimizelyUserId, attributes: optimizelyAttributes });
      updateOptimizelyInstance(_optimizelyInstance);
      updateOptimizelyUserId(_optimizelyUserId);
    }
  }, []);

  const text = getText(isProPage);
  const formFooter = <FormFooter text={text} />;

  return (
    <Wrapper text={text} foot={formFooter}>
      <OptimizelyMockSignupPageTest optimizelyInstance={optimizelyInstance}/>
      <RegistrationForm
        text={text}
        locale={locale}
        countryId={countryId}
        handleRegistrationSuccess={handleRegistrationSuccess}
        handleLoginClick={handleLoginClick}
        enableRecaptcha={enableRecaptcha}
        facebookAppId={facebookAppId}
        googleClientId={googleClientId}
        redirectTo={redirectTo}
        handleOptimizelyEvent={(eventType, eventOutcome, eventAttributes) => handleOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventType, eventOutcome, eventAttributes)}
      />
    </Wrapper>
  );
};

SignUpForm.propTypes = {
  isProPage: PropTypes.bool,
  locale: PropTypes.string,
  mobileBrowser: PropTypes.bool,
  optimizelySettings: PropTypes.object,
  countryId: PropTypes.number,
  handleRegistrationSuccess: PropTypes.func,
  redirectTo: PropTypes.string,
  handleLoginClick: PropTypes.func,
  enableRecaptcha: PropTypes.bool,
  facebookAppId: PropTypes.number.isRequired,
  googleClientId: PropTypes.string.isRequired
};

SignUpForm.defaultProps = {
  isProPage: false,
  handleRegistrationSuccess: () => {},
  handleLoginClick: null,
  locale: null,
  redirectTo: null
};

FormFooter.propTypes = {
  text: PropTypes.shape({
    privacyPolicyLead: PropTypes.string,
    termsLink: PropTypes.string,
    privacy: PropTypes.string
  })
};

FormFooter.defaultProps = {
  text: {
    privacyPolicyLead: 'Privacy Policy',
    termsLink: '/terms',
    privacy: 'Privacy'
  }
};

export default SignUpForm;
